import { DatePicker, Form, FormInstance, Input, Radio, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DateFormat } from "../../shared/constants";
import hrnUtils from "../../utils/hrnUtils";
import { useEffect, useState } from "react";
import { useGetBusinessTypesQuery } from "../../services/misc";
import { useLazySearchServiceCentersQuery } from "../../services/serviceCenter";
import { useLazyGetServiceCenterServiceTypesQuery } from "../../services/serviceType";
import { useSelector } from "react-redux";
import { RootState } from "../../store/types";
import TypeAheadInput from "../../components/TypeAheadInput";
import { SizeType } from "antd/es/config-provider/SizeContext";

const SerialForm = ({ form }: { form: FormInstance }) => {
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const { t } = useTranslation();
    const requiredRule = { required: true, message: t("Required") };
    const [businessTypeId, setBusinessTypeId] = useState<number | null>(null);
    const [selectedServiceCenter, setSelectedServiceCenter] = useState<any>(null);
    const [serviceTypes, setServiceTypes] = useState<any[]>([]);
    const [serviceTakerType, setServiceTakerType] = useState<string>("Self");
    const [getServiceTypes, getServiceTypesResult] = useLazyGetServiceCenterServiceTypesQuery();
    const [searchServiceCenters, searchServiceCentersResult] = useLazySearchServiceCentersQuery();
    const fieldSize: SizeType = "large";

    useEffect(() => {
        if (form.getFieldValue("mode") === "Edit") {
            setServiceTakerType(form.getFieldValue("serviceTaker"));
            getServiceTypes({ serviceCenterId: form.getFieldValue("serviceCenterId"), defaultIfNotSet: true }).unwrap();
        }
    }, [form.getFieldValue("mode")]);

    const {
        data: businessTypes,
        isFetching: isBTFetching,
        isSuccess: isBTSuccess,
    } = useGetBusinessTypesQuery({});

    const onBusinessTypeChanged = (value: number) => {
        setBusinessTypeId(value);
        form.resetFields(["serviceCenterId", "serviceTypeId"]);
    }

    const onServiceCenterChanged = async (value: string | null, option: any) => {
        if (!value) return;

        setServiceTypes([]);
        form.resetFields(["serviceTypeId"]);

        await getServiceTypes({ serviceCenterId: value, defaultIfNotSet: true }).unwrap();
        setSelectedServiceCenter(option.raw || option);
    };

    useEffect(() => {
        if (getServiceTypesResult.isSuccess) {
            setServiceTypes(getServiceTypesResult.data || []);
        }
    }, [getServiceTypesResult.isFetching]);

    return (
        <div>
            {form.getFieldValue("mode") !== "Edit" && <>
                <Form.Item name="businessTypeId" label={<>Service Provider Type<span style={{ color: "red", marginLeft: "2px" }}>*</span> </>} rules={[requiredRule]}>
                    <Select
                        size={fieldSize}
                        placeholder={"Service Provider Type"}
                        loading={isBTFetching}
                        options={isBTSuccess && businessTypes.map((x: any) => ({ label: x.name, value: x.id }))}
                        onChange={onBusinessTypeChanged}
                    />
                </Form.Item>
                <Form.Item name="serviceCenterId" label={<>{t("Service Center")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                    <TypeAheadInput
                        fetchOptions={async (query) => {
                            const res = await searchServiceCenters({ businessTypeId, searchText: query }).unwrap();
                            return res;
                        }}
                        mapOption={(center) => ({
                            value: center.id,
                            display: center.name,
                            label: (
                                <div>
                                    {center.company?.name && center.name !== center.company?.name && <>{center.company?.name}</>}
                                    <div><strong>{center.name}</strong></div>
                                    <div style={{ fontSize: 12, color: "#888" }}>{center.hotlineNo}</div>
                                </div>
                            ),
                            raw: center,
                        })}
                        placeholder="Search service centers..."
                        size={fieldSize}
                        loading={searchServiceCentersResult.isFetching || searchServiceCentersResult.isLoading}
                        onChange={onServiceCenterChanged}
                    />
                </Form.Item>
            </>}
            {form.getFieldValue("mode") === "Edit" && <Form.Item name={["serviceCenter", "name"]} label={<>Service Center <span style={{ color: "red", marginLeft: "2px" }}>*</span></>} >
                <Input size={fieldSize} placeholder="Name" disabled />
            </Form.Item>}
            <Form.Item name="serviceTypeId" label={<>{t("Service Type")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                <Select size={fieldSize} placeholder={t("Service Type")} options={serviceTypes.map((x) => ({ label: x.name, value: x.id }))} />
            </Form.Item>
            <Form.Item name="serviceDate" label={<>{t("Date")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]} initialValue={dayjs()}>
                <DatePicker size={fieldSize} disabled={form.getFieldValue("mode") === "Edit"} placeholder={t("Select date")} className="w-full" format={DateFormat.USUAL} disabledDate={(date: any) => date < dayjs().add(-1, "days") || date > dayjs().add((selectedServiceCenter?.daysOfAdvanceSerial || 0), "days")} onKeyDown={(e: any) => hrnUtils.handleDateFieldKeyDownEvent(e)} allowClear={false} />
            </Form.Item>
            <Form.Item name="serviceTaker" label={<>{t("For")}</>} rules={[requiredRule]} initialValue={serviceTakerType} >
                <Radio.Group size={fieldSize} options={["Self", "Other"].map((x) => ({ label: x, value: x }))} onChange={(e) => setServiceTakerType(e.target.value)} />
            </Form.Item>
            <Form.Item name="name" label={<>Name <span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]} initialValue={loggedInUser?.name} >
                <Input size={fieldSize} placeholder="Name" disabled={serviceTakerType === "Self" && !!loggedInUser?.name} />
            </Form.Item>
            <Form.Item name="contactNo" label={<>{t("Contact No.")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]} initialValue={loggedInUser?.mobileNo}>
                <Input inputMode="tel" size={fieldSize} placeholder="Contact No." disabled={serviceTakerType === "Self" && !!loggedInUser?.mobileNo} />
            </Form.Item>
        </div>
    );
};

export default SerialForm;
